import { Lock, Search, ClipboardList, MessageSquareQuote } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { PREMIERE_DATE } from "../config/site";
import { Dots } from "./Motifs";

const STEPS = [
    {
        n: "01",
        title: "Wybierasz sytuację",
        text: "Otwierasz lekcję o problemie, z którym właśnie się mierzysz.",
    },
    {
        n: "02",
        title: "Poznajesz przyczyny",
        text: "Rozumiesz, skąd bierze się trudność - u ucznia, w grupie, w ustawieniu zadania.",
    },
    {
        n: "03",
        title: "Dostajesz strategię",
        text: "Konkretny plan działania i gotowe reakcje, które możesz powiedzieć od razu.",
    },
    {
        n: "04",
        title: "Stosujesz na lekcji",
        text: "Wykorzystujesz rozwiązanie już przy najbliższych zajęciach.",
    },
];

const PREVIEWS = [
    { n: "16", topic: "Uczeń ciągle mówi: „Nie wiem”" },
    { n: "01", topic: "Jedna osoba dominuje rozmowę" },
    { n: "05", topic: "Uczniowie odpowiadają jednym zdaniem" },
];

const FORMAT = [
    {
        icon: Search,
        tint: "bg-hl-pink",
        label: "Dlaczego tak się dzieje",
        desc: "Poznajesz możliwe przyczyny zachowania ucznia.",
    },
    {
        icon: ClipboardList,
        tint: "bg-hl-cyan",
        label: "Strategia krok po kroku",
        desc: "Dostajesz konkretny plan reakcji na ten scenariusz.",
    },
    {
        icon: MessageSquareQuote,
        tint: "bg-tang",
        label: "Gotowe reakcje",
        desc: "Przykładowe zdania, które możesz powiedzieć od razu.",
    },
];

export const Value = () => (
    <section id="o-kursie" data-testid="value-framework-section" className="py-20 sm:py-28 bg-cream-deep relative overflow-hidden">
        <Dots className="absolute right-6 top-10 w-24 h-24 opacity-60 pointer-events-none hidden lg:block" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading
                kicker="Jak działa kurs"
                title="Otwierasz właściwą lekcję i wiesz, co zrobić"
            />

            <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
                {STEPS.map((s, i) => (
                    <Reveal key={s.n} delay={i * 0.1}>
                        <li data-testid={`value-step-card-${i + 1}`} className="relative">
                            <div className="flex items-center gap-4">
                                <span className="font-display text-4xl font-extrabold text-ink">
                                    {s.n}
                                </span>
                                {i < 3 && (
                                    <span className="hidden lg:block flex-1 h-px bg-gradient-to-r from-ink/20 to-transparent" />
                                )}
                            </div>
                            <h3 className="font-display mt-4 text-lg font-bold text-ink">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-sm text-ink-mute leading-relaxed">{s.text}</p>
                        </li>
                    </Reveal>
                ))}
            </ol>

            <Reveal delay={0.1}>
                <p className="mt-20 text-xs font-bold uppercase tracking-[0.22em] text-ink">
                    Zajrzyj do środka - każda lekcja ma ten sam układ
                </p>
            </Reveal>
            <div className="mt-6 grid md:grid-cols-3 gap-6">
                {PREVIEWS.map((p, i) => (
                    <Reveal key={p.n} delay={i * 0.1}>
                        <article
                            data-testid={`solution-preview-card-${i + 1}`}
                            className="h-full rounded-3xl border border-border bg-white p-6 flex flex-col shadow-soft transition-all duration-300 hover:shadow-card hover:-translate-y-1"
                        >
                            <span className="self-start rounded-full bg-cream-deep px-3.5 py-1.5 font-display text-xs font-extrabold uppercase tracking-wider text-ink">
                                Lekcja {p.n}
                            </span>
                            <h3 className="font-display mt-4 text-xl font-bold text-ink leading-snug">
                                {p.topic}
                            </h3>
                            <div className="mt-5 space-y-4 flex-1">
                                {FORMAT.map((f) => (
                                    <div key={f.label} className="flex gap-3 items-start">
                                        <span
                                            className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full text-ink ${f.tint}`}
                                        >
                                            <f.icon className="w-5 h-5" />
                                        </span>
                                        <div>
                                            <p className="font-display font-bold text-sm text-ink">
                                                {f.label}
                                            </p>
                                            <p className="mt-0.5 text-xs text-ink-mute leading-relaxed">
                                                {f.desc}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-5 pt-4 border-t border-border text-xs text-ink-mute flex items-center gap-1.5">
                                <Lock className="w-3 h-3" />
                                Pełna treść lekcji - w kursie od {PREMIERE_DATE}
                            </p>
                        </article>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
